// src/pages/settings/Profile.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import CommonFooter from "../../core/common/footer/commonFooter";
import {
  updateUserProfile,
  getUserProfile,
  deleteUser,
  exportProductsExcel,
} from "../../api/userApi";
import { uploadCatalogImage } from "../../api/productApi"; // ✅ reuse uploader
import { toast } from "react-toastify";
import PropTypes from "prop-types";

// ---- helpers (same as supplier/biller semantics) -----------------------------
const normalizeKey = (s) => {
  if (!s) return "";
  let v = String(s).trim();
  v = v.replace(/^\/+/, "");
  v = v.replace(/^catelog\//i, "catalog/");
  if (!/^catalog\//i.test(v)) v = `catalog/${v.split("/").pop()}`;
  return v;
};

const toPublicUrl = (v) => {
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  const key = normalizeKey(v);
  const base = (
    process.env.REACT_APP_API_IMAGE_BASE_URL ||
    "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
  ).replace(/\/+$/, "");
  return `${base}/${key}`;
};

const buildUserFileName = (file, first, last, username, email) => {
  const ext =
    (file.type && file.type.split("/")[1]) ||
    file.name.split(".").pop() ||
    "jpg";
  const stamp = new Date()
    .toISOString()
    .replace(/[-:.TZ]/g, "")
    .slice(0, 14);
  const base = (first || last || username || email || "user")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "");
  // e.g., USER_ai-hamilton_20251112094500.jpg
  return `USER_${base || "user"}_${stamp}.${ext}`;
};
// -----------------------------------------------------------------------------

const Profile = () => {
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // ✅ image: preview URL + S3 key (send key to backend)
  const [profileImagePreview, setProfileImagePreview] = useState("");
  const [profileImageKey, setProfileImageKey] = useState("");

  const [usage, setUsage] = useState({ limit: 0, used: 0, remaining: 0 });
  const [seatUsage, setSeatUsage] = useState({
    limit: 1,
    used: 0,
    remaining: 1,
  });
  const [storeUsage, setStoreUsage] = useState({
    limit: 1,
    used: 0,
    remaining: 1,
  });
  const [warehouseUsage, setWarehouseUsage] = useState({
    limit: 1,
    used: 0,
    remaining: 1,
  });
  const [showStoresCard, setShowStoresCard] = useState(false);
  const Navigate = useNavigate();

  // ⏬ Fetch user data on mount
  useEffect(() => {
    const fetchProfile = async () => {
      const result = await getUserProfile();
      if (!result.success) {
        alert(window.translate("Failed to load user profile"));
        return;
      }
      const data = result.data || {};

      setFirstName(data.firstName || "");
      setLastName(data.lastName || "");
      setUsername(data.username || "");
      setPhone(data.phone || "");
      setEmail(data.email || "");

      // 🔄 Normalize existing profile image -> preview + key
      const existing = data.profileImage || data.profileImageKey || "";
      setProfileImageKey(existing ? normalizeKey(existing) : "");
      setProfileImagePreview(existing ? toPublicUrl(existing) : "");

      // ---- usage blocks
      const u = data.usage || {};
      setUsage({
        limit: u.product?.limit ?? 0,
        used: u.product?.used ?? 0,
        remaining:
          u.product?.limit === 0
            ? null
            : Math.max((u.product?.limit || 0) - (u.product?.used || 0), 0),
      });
      setSeatUsage({
        limit: u.users?.limit ?? 1,
        used: u.users?.used ?? 0,
        remaining:
          u.users?.limit === 0
            ? null
            : Math.max((u.users?.limit || 0) - (u.users?.used || 0), 0),
      });

      const hasStores = !!u.stores;
      setShowStoresCard(hasStores);
      if (hasStores) {
        setStoreUsage({
          limit: u.stores?.limit ?? 1,
          used: u.stores?.used ?? 0,
          remaining:
            u.stores?.limit === 0
              ? null
              : Math.max((u.stores?.limit || 0) - (u.stores?.used || 0), 0),
        });
      } else {
        setStoreUsage({ limit: 0, used: 0, remaining: 0 });
      }

      setWarehouseUsage({
        limit: u.warehouses?.limit ?? 1,
        used: u.warehouses?.used ?? 0,
        remaining:
          u.warehouses?.limit === 0
            ? null
            : Math.max(
                (u.warehouses?.limit || 0) - (u.warehouses?.used || 0),
                0
              ),
      });
    };

    fetchProfile();
  }, []);

  const handleSaveProfile = async () => {
    const profileData = {
      firstName,
      lastName,
      username,
      phone,
      email,
      password,
      // ✅ send S3 key only; fallback to normalized preview if user never changed it
      profileImage: profileImageKey || normalizeKey(profileImagePreview || ""),
    };

    const result = await updateUserProfile(profileData);
    if (result.success) {
      alert(window.translate("Profile updated successfully"));
      Navigate("/index");
    } else {
      alert(window.translate(`Profile Update failed: ${result.error}`));
    }
  };

  // ✅ Upload to S3 immediately on selection (like supplier)
  // ✅ Upload to S3 immediately on selection (like supplier) — fixed to await DataURL
  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // (optional) max 2MB guard
    if (file.size > 2 * 1024 * 1024) {
      toast.error(window.translate("Image too large (max 2 MB)"));
      return;
    }

    try {
      // 1) Read file as data URL (await!)
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Show immediate preview
      setProfileImagePreview(dataUrl);

      // 2) Build a good filename
      const desired = buildUserFileName(
        file,
        firstName,
        lastName,
        username,
        email
      );

      // 3) Call your uploader with the **correct shape**
      // If your helper expects positional args:
      // const { key } = await uploadCatalogImage(dataUrl, desired);

      // If your helper expects an object (most common in these backends):
      const { key } = await uploadCatalogImage(dataUrl, desired);

      // 4) Update state with normalized key + public URL
      const k = normalizeKey(key);
      setProfileImageKey(k);
      setProfileImagePreview(toPublicUrl(k));
    } catch (err) {
      console.error(err);
      toast.error(window.translate("Image upload failed"));
      setProfileImagePreview(
        profileImageKey ? toPublicUrl(profileImageKey) : ""
      );
    }
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible((prevState) => !prevState);
  };

  const handleDeleteAccount = async () => {
    if (
      window.confirm(
        window.translate(
          "Are you sure you want to delete your account? This action cannot be undone."
        )
      )
    ) {
      const result = await deleteUser();
      if (result.success) {
        alert(window.translate("Account deleted successfully"));
        localStorage.removeItem("token");
        localStorage.removeItem("authToken");
        window.location.href = "/login";
      } else {
        alert(window.translate(`Error in delete profile: ${result.error}`));
      }
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const { data } = await exportProductsExcel();
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `products_audit_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Excel download failed:", err);
      alert(window.translate("Failed to download. Please try again."));
    }
  };

  const UsageCard = ({ iconClass, title, limit, used }) => {
    const percent =
      limit === 0
        ? 0
        : Math.min(Math.round((used / Math.max(limit, 1)) * 100), 100);

    UsageCard.propTypes = {
      iconClass: PropTypes.string.isRequired,
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
      limit: PropTypes.number.isRequired,
      used: PropTypes.number.isRequired,
    };

    return (
      <div className="card shadow-sm border-0 h-100">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center">
              <span
                className="rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: 36, height: 36, background: "#FFF5E6" }}
              >
                <i className={`ti ${iconClass} text-warning fs-6`} />
              </span>
              <div>
                <div className="fw-semibold">{title}</div>
                <div className="text-muted small">
                  {window.translate("Track your inventory capacity")}
                </div>
              </div>
            </div>
            <span className="badge rounded-pill bg-warning-subtle text-warning border">
              {limit === 0
                ? window.translate("Unlimited")
                : `${limit} ${window.translate("Limit")}`}
            </span>
          </div>

          {limit === 0 ? (
            <div className="text-muted small">
              {window.translate("No limit applied to this account.")}
            </div>
          ) : (
            <>
              <div className="progress" style={{ height: 10 }}>
                <div
                  className="progress-bar bg-warning"
                  role="progressbar"
                  style={{ width: `${percent}%` }}
                  aria-valuenow={used}
                  aria-valuemin={0}
                  aria-valuemax={limit}
                />
              </div>
              <div className="d-flex justify-content-between mt-2 small">
                <span className="text-muted">
                  {window.translate("Used")}:{" "}
                  <strong className="text-dark">{used}</strong>
                </span>
                <span className="text-muted">
                  {window.translate("Remaining")}:{" "}
                  <strong className="text-dark">
                    {limit === 0
                      ? "—"
                      : Math.max((limit || 0) - (used || 0), 0)}
                  </strong>
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="page-header mb-3">
          <div className="page-title">
            <h4 className="mb-1">{window.translate("Profile")}</h4>
            <h6 className="text-muted m-0">
              {window.translate("User Profile")}
            </h6>
          </div>
          <div className="d-flex justify-content-end mb-3 gap-2">
            <Link to="/settings/users" className="btn btn-primary">
              <i className="ti ti-users-plus me-2 ms-2" />
              {window.translate("Manage Users & Permissions")}
            </Link>
            <button
              className="btn btn-outline-primary"
              onClick={handleDownloadExcel}
            >
              <i className="ti ti-download me-1" />{" "}
              {window.translate("Download Product Audit")}
              {window.translate("Excel")}
            </button>
          </div>
        </div>

        <div className="ms-auto d-none d-md-flex align-items-center gap-2 mb-3">
          <span className="badge bg-light text-dark border">
            {window.translate("ID")}: <strong className="ms-1">{email}</strong>
          </span>
        </div>

        {/* Usage + Quick identity */}
        <div className="row g-3 mb-4">
          {/* Product Usage Card */}
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center">
                    <span
                      className="rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                      style={{ width: 36, height: 36, background: "#FFF5E6" }}
                    >
                      <i className="ti ti-package text-warning fs-6" />
                    </span>
                    <div>
                      <div className="fw-semibold">
                        {window.translate("Product Usage")}
                      </div>
                      <div className="text-muted small">
                        {window.translate("Track your inventory capacity")}
                      </div>
                    </div>
                  </div>
                  <span className="badge rounded-pill bg-warning-subtle text-warning border">
                    {usage.limit === 0 ? "Unlimited" : `${usage.limit} limit`}
                  </span>
                </div>

                {usage.limit === 0 ? (
                  <div className="text-muted small">
                    {window.translate("No limit applied to this account.")}
                  </div>
                ) : (
                  <>
                    <div className="progress" style={{ height: 10 }}>
                      <div
                        className="progress-bar bg-warning"
                        role="progressbar"
                        style={{
                          width: `${Math.min(
                            Math.round(
                              (usage.used / Math.max(usage.limit, 1)) * 100
                            ),
                            100
                          )}%`,
                        }}
                        aria-valuenow={usage.used}
                        aria-valuemin={0}
                        aria-valuemax={usage.limit}
                      />
                    </div>
                    <div className="d-flex justify-content-between mt-2 small">
                      <span className="text-muted">
                        {window.translate("Used")}:{" "}
                        <strong className="text-dark">{usage.used}</strong>
                      </span>
                      <span className="text-muted">
                        {window.translate("Remaining")}:{" "}
                        <strong className="text-dark">
                          {usage.limit === 0 ? "—" : usage.remaining}
                        </strong>
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Identity / avatar */}
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body d-flex align-items-center">
                <div className="me-3">
                  <div
                    className="rounded-circle overflow-hidden border"
                    style={{ width: 64, height: 64, background: "#f3f6f8" }}
                  >
                    {profileImagePreview ? (
                      <img
                        src={profileImagePreview}
                        alt="Avatar"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted">
                        <i className="ti ti-user fs-3" />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-grow-1">
                  <div className="fw-semibold">
                    {firstName} {lastName}{" "}
                    <span className="text-muted">@{username}</span>
                  </div>
                  <div className="text-muted small">{email}</div>
                  <div className="mt-2">
                    <label className="btn btn-sm btn-outline-secondary shadow-none mb-0">
                      {window.translate("Change Avatar")}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        hidden
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* NEW: Seats / Stores / Warehouses usage */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-4">
            <UsageCard
              iconClass="ti-users"
              title={window.translate("User Usage")}
              limit={seatUsage.limit}
              used={seatUsage.used}
            />
          </div>
          {showStoresCard && (
            <div className="col-12 col-lg-4">
              <UsageCard
                iconClass="ti-building-store"
                title={window.translate("Store Usage")}
                limit={storeUsage.limit}
                used={storeUsage.used}
              />
            </div>
          )}
          <div className="col-12 col-lg-4">
            <UsageCard
              iconClass="ti-building-warehouse"
              title={window.translate("Warehouse Usage")}
              limit={warehouseUsage.limit}
              used={warehouseUsage.used}
            />
          </div>
        </div>

        {/* Profile form */}
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white">
            <h5 className="mb-0 d-flex align-items-center">
              <i className="ti ti-user-circle text-primary me-2" />
              {window.translate("Basic Information")}
            </h5>
          </div>

          <div className="card-body">
            <div className="row g-3">
              <div className="col-lg-6">
                <label className="form-label">
                  {window.translate("First Name")}
                  <span className="text-danger ms-1">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>

              <div className="col-lg-6">
                <label className="form-label">
                  {window.translate("Last Name")}
                  <span className="text-danger ms-1">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>

              <div className="col-lg-6">
                <label className="form-label">
                  {window.translate("Email")}
                  <span className="text-danger ms-1">*</span>
                </label>
                <input
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="col-lg-6">
                <label className="form-label">
                  {window.translate("Phone Number")}
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>

              <div className="col-lg-6">
                <label className="form-label">
                  {window.translate("User Name")}
                  <span className="text-danger ms-1">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>

              <div className="col-lg-6">
                <label className="form-label">
                  {window.translate("Password")}
                </label>
                <div className="input-group">
                  <input
                    type={isPasswordVisible ? "text" : "password"}
                    className="form-control"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={togglePasswordVisibility}
                    title={isPasswordVisible ? "Hide" : "Show"}
                  >
                    <i
                      className={`ti ${
                        isPasswordVisible ? "ti-eye-off" : "ti-eye"
                      }`}
                    />
                  </button>
                </div>
                <div className="form-text"></div>
              </div>
            </div>
          </div>

          <div className="card-footer bg-white d-flex justify-content-end gap-2">
            <Link to="/index" className="btn btn-light border shadow-none">
              {window.translate("Cancel")}
            </Link>
            <button
              className="btn btn-primary shadow-none"
              onClick={handleSaveProfile}
            >
              {window.translate("Save Changes")}
            </button>
            <button
              className="btn btn-outline-danger shadow-none"
              onClick={handleDeleteAccount}
            >
              {window.translate("Delete Account")}
            </button>
          </div>
        </div>
      </div>

      <CommonFooter />
    </div>
  );
};

export default Profile;
